/* ==========================================================================
   Tent finder — Fiaz's quiz and recommendation engine (his tent-finder-app.js,
   tent-finder-questions.js and tent-finder-products.js as one file), wrapped so
   none of its names reach the page, and fed by the JSON block that
   sections/bc-tent-finder.liquid outputs from the "Tent finder –" metaobjects.
   tent-finder-plan.md, Stage 6.

   The flow, timings, scoring, markup and timer are his. The agreed changes are
   marked "Change:". Everything is matched on the fixed codes from admin, never
   on English wording.
   ========================================================================== */
(function () {
  "use strict";

  const app = document.getElementById("tent-finder-app-mount");
  const dataEl = document.getElementById("bc-tent-finder-data");
  if (!app || !dataEl) return;

  let DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  const COPY = DATA.copy || {};
  const OFFER = DATA.offer || null;

  function esc(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  }

  /* ---------------- DATA: fixed admin codes → Fiaz's internal ids ---------------- */
  const QUESTION_ID = { size: "size", use: "usecase", layout: "layout", structure: "structure", budget: "budget" };
  const ANSWER_ID = {
    "size-1-2": "1-2", "size-3-4": "3-4", "size-5-6": "5-6", "size-7-plus": "7+",
    "use-festival": "festival", "use-weekend": "weekend", "use-weeklong": "weeklong", "use-commercial": "commercial",
    "layout-classic": "classic", "layout-family": "family", "layout-statement": "statement", "layout-premium": "premium",
    "structure-inflatable": "air", "structure-poles": "pole", "structure-either": "neither",
    "budget-under-500": "under500", "budget-over-500": "over500", "budget-any": "nomind",
  };
  const FABRIC_ID = {
    "oxford-175": "oxford175", "polycotton-200": "polycotton200", "polycotton-285": "polycotton285",
    "cotton-285": "cotton285", "fireproof-320": "fireproof320",
  };
  const ICON_ID = { people: "people2", coin: "coin1", gem: "coin4", tick: "checkcircle" };

  // An answer only counts under its own question type ("use-…" under "use"), so a
  // mis-filed answer is left out rather than scored against the wrong question.
  const QUESTIONS = (DATA.questions || [])
    .filter((q) => QUESTION_ID[q.type])
    .map((q) => ({
      id: QUESTION_ID[q.type],
      title: q.title,
      subtitle: q.subtitle,
      multi: false,
      options: (q.answers || [])
        .filter((a) => ANSWER_ID[a.type] && a.type.indexOf(q.type + "-") === 0)
        .map((a) => ({
          id: ANSWER_ID[a.type],
          label: a.label,
          // Change: the words a greyed-out note uses; blank falls back to the label.
          greyed: a.greyedWording || a.label,
          img: a.photo || null,
          icon: ICON_ID[a.icon] || null,
        })),
    }));

  // The scoring needs all five questions, each with answers. Fail closed rather
  // than render a quiz that scores NaN.
  const ALL_QUESTIONS = Object.keys(QUESTION_ID).map((k) => QUESTION_ID[k]);
  if (!ALL_QUESTIONS.every((id) => QUESTIONS.some((q) => q.id === id && q.options.length))) return;

  // Change: sold-out tents are filtered out FIRST — before scoring, the
  // two-per-product rule and the greying — so one can never be shown, and never
  // keeps a dead end from being greyed. Then Position order, which is Fiaz's
  // array order and so keeps his tie-breaks.
  const PRODUCTS = (DATA.tents || [])
    .filter((t) => t.available === true && FABRIC_ID[t.fabric])
    .sort((a, b) => a.position - b.position)
    .map((t) => ({
      id: t.handle,
      name: t.name,
      // Change: "two per family" is two per product.
      family: t.productId,
      size: t.sizeLabel,
      capacity: t.sleeps,
      fabric: FABRIC_ID[t.fabric],
      fabricLabel: t.fabricLabel,
      structure: t.structure,
      festival: t.festival === true,
      premium: t.premium === true,
      layoutTags: t.layouts || [],
      quickPitch: t.pitchSpeed,
      // Budget bands and the out-of-band penalty are in whole units, as Fiaz's were.
      price: t.price / 100,
      priceText: t.priceText,
      wasPriceText: t.wasPriceText,
      image: t.photo,
      // Change: the exact variant, with the storefront's market path.
      url: t.url,
    }));

  /* ---------------- ICONS (stroke, matches V2's line-icon convention) ---------------- */
  const ICONS = {
    check: '<path d="M4 12.5 9.5 18 20 6"/>',
    arrowLeft: '<path d="M19 12H5M11 6l-6 6 6 6"/>',
    arrowRight: '<path d="M5 12h14M13 6l6 6-6 6"/>',
    medal: '<circle cx="12" cy="14" r="6.5"/><path d="M9.5 8.5 6.5 2.5M14.5 8.5l3-6M12 11.2l1.1 2.3 2.5.3-1.8 1.7.4 2.5-2.2-1.2-2.2 1.2.4-2.5-1.8-1.7 2.5-.3Z"/>',
    clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5.3l3.6 2.1"/>',
    tent: '<path d="M3 20 12 4l9 16H3Z"/><path d="M12 10 8.5 20M12 10l3.5 10"/>',
    coin1: '<circle cx="12" cy="14" r="6"/><path d="M12 10.5v7M9.6 15.5c0 1 1 1.6 2.4 1.6s2.4-.6 2.4-1.7c0-2.4-4.8-1.2-4.8-3.6 0-1.1 1-1.7 2.4-1.7s2.4.6 2.4 1.6"/>',
    coin4: '<path d="M4 9 8 4h8l4 5-9 11.5Z"/><path d="M4 9h16M8.5 4l-.8 5M15.5 4l.8 5M9 9l3 10.5 3-10.5"/>',
    checkcircle: '<circle cx="12" cy="12" r="9"/><path d="M8.5 12.5 11 15l4.5-6"/>',
  };
  const SOLID_ICONS = {
    people2: '<g opacity=".45"><circle cx="15.6" cy="7.4" r="3.1"/><path d="M9.3 20c.4-4.3 3.1-6.8 6.5-6.8s6.1 2.5 6.5 6.8a.9.9 0 0 1-.9 1H10.2a.9.9 0 0 1-.9-1Z"/></g><circle cx="9.1" cy="8.5" r="3.5"/><path d="M2 20.6c.5-4.6 3.4-7.3 7.1-7.3s6.7 2.7 7.1 7.3a1 1 0 0 1-1 1.1H3a1 1 0 0 1-1-1.1Z"/>',
  };
  function icon(name, cls = "ic") {
    return `<svg class="${cls}" viewBox="0 0 24 24" fill="none" stroke-linecap="round" stroke-linejoin="round">${ICONS[name] || ""}</svg>`;
  }
  function chipIcon(name) {
    if (SOLID_ICONS[name]) {
      return `<svg class="ic-solid" viewBox="0 0 24 24">${SOLID_ICONS[name]}</svg>`;
    }
    return `<svg viewBox="0 0 24 24" fill="none" stroke-linecap="round" stroke-linejoin="round">${ICONS[name] || ""}</svg>`;
  }

  /* ---------------- STATE ---------------- */
  const state = {
    step: "intro", // intro | q | loading | results
    qIndex: 0,
    answers: {},
  };
  let urgencyInterval = null;

  /* ---------------- SCORING ENGINE ---------------- */
  const SIZE_TARGET = { "1-2": 2, "3-4": 4, "5-6": 6, "7+": 9 };
  const BUDGET_BANDS = {
    under500: [0, 500],
    over500: [500, 999999],
    nomind: [0, 999999],
  };
  const USECASE_FABRIC_POINTS = {
    festival: { oxford175: 5, polycotton200: 2.5 },
    weekend: { polycotton200: 5, polycotton285: 2.5 },
    weeklong: { polycotton285: 5, cotton285: 4 },
    commercial: { fireproof320: 7 },
  };
  const USECASE_LABEL = {
    festival: "festival camping",
    weekend: "short weekend getaways",
    weeklong: "week-long trips",
    commercial: "commercial & trade use",
  };
  const LAYOUT_REASON = {
    classic: "Simple, classic single-pole design",
    family: "Multiple doors — great for spreading out",
    statement: "A standout design that makes a statement",
    premium: "Upgraded frame for extra headroom & comfort",
  };

  // Change: "premium" comes from the tent entry's Premium tick, not a layout tag.
  function matchesLayout(product, layout) {
    return layout === "premium" ? product.premium : product.layoutTags.includes(layout);
  }

  function scoreProduct(product, answers) {
    let score = 0;
    const reasons = []; // { text, weight }

    const target = SIZE_TARGET[answers.size];
    const diff = Math.abs(product.capacity - target);
    score += Math.max(0, 12 - diff * 2.2);
    if (diff <= 1) {
      reasons.push({ text: `Comfortably suited to ${sizeLabel(answers.size)} people`, weight: 6 });
    } else if (diff <= 2) {
      reasons.push({ text: `Sleeps up to ${product.capacity} — a good fit for your group`, weight: 3 });
    }

    const fabricPts = (USECASE_FABRIC_POINTS[answers.usecase] || {})[product.fabric] || 0;
    score += fabricPts;
    // Change: the festival bonus reads the Festival tent tick (was: tent type "festival").
    if (answers.usecase === "festival" && (product.festival || product.quickPitch >= 2)) score += 2.5;
    if (answers.usecase === "commercial" && product.fabric === "fireproof320") score += 2;
    if (fabricPts >= 4) {
      reasons.push({ text: `${product.fabricLabel} — a great match for ${USECASE_LABEL[answers.usecase]}`, weight: 5 });
    }

    if (matchesLayout(product, answers.layout)) {
      score += 5;
      reasons.push({ text: LAYOUT_REASON[answers.layout], weight: 4 });
    }

    // Change: structure reads the tent's Structure field (was: tent type, where
    // "festival" counted as inflatable — wrong for the Multicolour Rainbow).
    if (answers.structure === "air") {
      if (product.structure === "inflatable") {
        score += 6;
        reasons.push({ text: "Pole-free inflatable design — up in minutes", weight: 5 });
      } else {
        score -= 2;
      }
    } else if (answers.structure === "pole") {
      if (product.structure === "poles") {
        score += 6;
        reasons.push({ text: "Classic pole-pitched bell tent design", weight: 4 });
      } else {
        score -= 2;
      }
    }

    const [lo, hi] = BUDGET_BANDS[answers.budget];
    if (product.price >= lo && product.price <= hi) {
      score += 4.5;
      reasons.push({ text: "Fits comfortably within your budget", weight: 2 });
    } else {
      const dist = product.price < lo ? lo - product.price : product.price - hi;
      score += Math.max(-6, 4.5 - dist / 120);
    }

    reasons.sort((a, b) => b.weight - a.weight);
    return { score, reasons: reasons.map((r) => r.text) };
  }

  function sizeLabel(id) {
    return { "1-2": "1–2", "3-4": "3–4", "5-6": "5–6", "7+": "7+" }[id];
  }

  function getRecommendations(answers) {
    let pool = PRODUCTS;

    // Commercial & trade is a genuine requirement, not a soft preference —
    // fire-certified fabric is non-negotiable for that use case.
    if (answers.usecase === "commercial") {
      pool = pool.filter((p) => p.fabric === "fireproof320");
    }
    // Fireproof cotton is heavy, commercial-grade canvas — the opposite of
    // what festival camping needs, so it's excluded outright.
    if (answers.usecase === "festival") {
      pool = pool.filter((p) => p.fabric !== "fireproof320");
    }
    // A classic-style tent is a deliberately simple, unadorned design — it's
    // never the right recommendation when someone asks for a statement piece.
    // Match on the "classic" layout tag, not the family name, so anything
    // classic-silhouette (Classic Bell, Multicolour Rainbow, etc.) is excluded.
    if (answers.layout === "statement") {
      pool = pool.filter((p) => !p.layoutTags.includes("classic"));
    }
    // "Extra headroom & premium" means our genuinely elevated designs only.
    // Change: the tent entry's Premium tick (was: four typed family names).
    if (answers.layout === "premium") {
      pool = pool.filter((p) => p.premium);
    }
    // Budget is a genuine constraint, not a soft preference — "Don't Mind"
    // leaves everything in play, but Under/Over £500 must be enforced.
    if (answers.budget && answers.budget !== "nomind") {
      const [lo, hi] = BUDGET_BANDS[answers.budget];
      pool = pool.filter((p) => p.price >= lo && p.price <= hi);
    }

    const scored = pool.map((p) => ({ product: p, ...scoreProduct(p, answers) }));
    scored.sort((a, b) => b.score - a.score);

    const picked = [];
    const familyCount = {};
    for (const entry of scored) {
      const fam = entry.product.family;
      const count = familyCount[fam] || 0;
      if (count < 2) {
        picked.push(entry);
        familyCount[fam] = count + 1;
      }
      if (picked.length >= 7) break;
    }
    if (picked.length < 7) {
      for (const entry of scored) {
        if (picked.length >= 7) break;
        if (!picked.includes(entry)) picked.push(entry);
      }
    }
    // Only ever show tents that are a genuine match — never pad the grid
    // with irrelevant products just to hit a round number.
    return picked.filter((entry) => entry.score > 0).slice(0, 7);
  }

  /* ---------------- DEAD ENDS ---------------- */
  // Change: an answer is greyed out when no way of answering the questions after
  // it returns a tent (was: one typed rule, Statement + Under £500). Worked out
  // from the live data, so a size selling out can never leave a dead end open.
  // Only the questions BEFORE this one count; later answers are re-checked, and
  // cleared if stale, when the shopper reaches them — as Fiaz's were.
  // True when some way of answering every question not fixed in `fixed`, from
  // question `fromIndex` on, returns at least one tent.
  function hasMatch(fixed, fromIndex = 0) {
    if (fromIndex >= QUESTIONS.length) return getRecommendations(fixed).length > 0;
    const q = QUESTIONS[fromIndex];
    if (fixed[q.id]) return hasMatch(fixed, fromIndex + 1);
    return q.options.some((o) => hasMatch({ ...fixed, [q.id]: o.id }, fromIndex + 1));
  }

  function answersBefore(index, answers) {
    const before = {};
    for (let i = 0; i < index; i++) {
      const id = QUESTIONS[i].id;
      if (answers[id]) before[id] = answers[id];
    }
    return before;
  }

  // Which earlier answer to name, latest question first:
  //   1. one that rules this answer out on its own, whatever else is picked —
  //      Fiaz's own case: Statement Design + Under £500;
  //   2. else one whose change alone would open this answer up;
  //   3. else the latest earlier answer.
  function blockingAnswer(index, opt, before) {
    const q = QUESTIONS[index];
    const earlier = [];
    for (let i = index - 1; i >= 0; i--) {
      const eq = QUESTIONS[i];
      const current = eq.options.find((o) => o.id === before[eq.id]);
      if (current) earlier.push({ eq, current });
    }
    const alone = earlier.find(({ eq, current }) => !hasMatch({ [eq.id]: current.id, [q.id]: opt.id }));
    if (alone) return alone.current;
    const change = earlier.find(({ eq, current }) =>
      eq.options.some((o) => o.id !== current.id && hasMatch({ ...before, [eq.id]: o.id, [q.id]: opt.id }))
    );
    if (change) return change.current;
    return earlier.length ? earlier[0].current : null;
  }

  // Fiaz's wording: "No [earlier answer] tents are available [this answer]", as in
  // "No Statement Design tents are available under £500". That reads only when the
  // greyed answer is a budget; any other greyed answer is named first and the
  // earlier one follows "for": "No Extra Headroom & Premium tents are available
  // for Commercial & Trade".
  function getDisabledReason(index, opt, answers) {
    const q = QUESTIONS[index];
    const before = answersBefore(index, answers);
    if (hasMatch({ ...before, [q.id]: opt.id }, index + 1)) return null;

    const blocker = blockingAnswer(index, opt, before);
    if (!blocker) return `No tents are available ${opt.greyed}`;
    return q.id === "budget"
      ? `No ${blocker.greyed} tents are available ${opt.greyed}`
      : `No ${opt.greyed} tents are available for ${blocker.greyed}`;
  }

  /* ---------------- RENDER: SHELL ---------------- */
  function render() {
    if (urgencyInterval) { clearInterval(urgencyInterval); urgencyInterval = null; }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    if (state.step === "intro") return renderIntro();
    if (state.step === "loading") return renderLoading();
    if (state.step === "results") return renderResults();
    return renderQuestion(state.qIndex);
  }

  function renderIntro() {
    app.innerHTML = `
      <section class="tf-intro">
        <div class="tf-intro__eyebrow">${esc(COPY.introEyebrow)}</div>
        <h1>${esc(COPY.introHeading)}</h1>
        <p class="tf-intro__sub">${esc(COPY.introText)}</p>
        <div class="tf-intro__hero">
          <img src="${esc(COPY.introPhoto)}" alt="${esc(COPY.introPhotoAlt)}" />
        </div>
        <button class="btn btn-primary" id="start-btn">${esc(COPY.startButton)} ${icon("arrowRight", "ic")}</button>
        <div class="tf-intro__meta">
          <span>${icon("clock")} ${esc(COPY.introTime)}</span>
          <span>${icon("tent")} ${esc(COPY.introQuestions)}</span>
          <span>${icon("medal")} ${esc(COPY.introMatches)}</span>
        </div>
      </section>
    `;
    app.querySelector("#start-btn").addEventListener("click", () => {
      state.step = "q";
      state.qIndex = 0;
      render();
    });
  }

  function renderQuestion(index) {
    const q = QUESTIONS[index];
    const total = QUESTIONS.length;

    // Clear a stale answer that's no longer valid — e.g. the user picked
    // Under £500, went back, and switched layout to Statement Design.
    const current = q.options.find((o) => o.id === state.answers[q.id]);
    if (state.answers[q.id] && (!current || getDisabledReason(index, current, state.answers))) {
      delete state.answers[q.id];
    }
    const selected = state.answers[q.id];

    const steps = QUESTIONS.map((_, i) => {
      const cls = i === index ? "active" : i < index ? "done" : "";
      return `<div class="step ${cls}">${i + 1}</div>${i < total - 1 ? `<div class="track ${i < index ? "done" : ""}"></div>` : ""}`;
    }).join("");

    app.innerHTML = `
      <div class="quiz-progress-wrap">
        <div class="quiz-progress">${steps}</div>
        <div class="quiz-progress-label" style="margin-top:12px;">Question ${index + 1} of ${total}</div>
      </div>
      <section class="tf-question">
        <div class="tf-question__head">
          <h2 class="tf-question__title">${esc(q.title)}</h2>
          <p class="tf-question__sub">${esc(q.subtitle)}</p>
        </div>
        <div class="chip-grid">
          ${q.options.map((opt) => renderChip(opt, selected, getDisabledReason(index, opt, state.answers))).join("")}
        </div>
        <div class="tf-nav">
          <button class="btn btn-ghost" id="back-btn" ${index === 0 ? "style='visibility:hidden'" : ""}>${icon("arrowLeft")} Back</button>
        </div>
      </section>
    `;

    app.querySelectorAll(".model-chip").forEach((btn) => {
      btn.addEventListener("click", () => {
        state.answers[q.id] = btn.dataset.opt;
        renderQuestion(index);
        setTimeout(() => goNext(index), 260);
      });
    });

    const backBtn = app.querySelector("#back-btn");
    if (backBtn && index > 0) backBtn.addEventListener("click", () => goBack(index));
  }

  function renderChip(opt, selectedId, disabledReason) {
    const isSelected = selectedId === opt.id;
    const isDisabled = !!disabledReason;
    const checkEl = `<span class="mc-check">${icon("check")}</span>`;
    const disabledAttr = isDisabled ? "disabled" : "";
    const noteEl = isDisabled ? `<span class="mc-disabled-note">${esc(disabledReason)}</span>` : "";
    if (opt.img) {
      return `
        <button class="model-chip ${isSelected ? "is-selected" : ""} ${isDisabled ? "is-disabled" : ""}" data-opt="${esc(opt.id)}" ${disabledAttr}>
          ${checkEl}
          <span class="mc-img"><img src="${esc(opt.img)}" alt="" loading="lazy"></span>
          <span class="mc-name">${esc(opt.label)}</span>
          ${noteEl}
        </button>
      `;
    }
    return `
      <button class="model-chip model-chip--icon ${isSelected ? "is-selected" : ""} ${isDisabled ? "is-disabled" : ""}" data-opt="${esc(opt.id)}" ${disabledAttr}>
        ${checkEl}
        <span class="mc-icon-wrap">${chipIcon(opt.icon)}</span>
        <span class="mc-name">${esc(opt.label)}</span>
        ${noteEl}
      </button>
    `;
  }

  function goNext(index) {
    const q = QUESTIONS[index];
    if (!state.answers[q.id]) return;
    if (index === QUESTIONS.length - 1) {
      state.step = "loading";
      render();
      setTimeout(() => {
        state.step = "results";
        render();
      }, 1400);
      return;
    }
    state.qIndex = index + 1;
    render();
  }

  function goBack(index) {
    if (index === 0) {
      state.step = "intro";
    } else {
      state.qIndex = index - 1;
    }
    render();
  }

  function renderLoading() {
    app.innerHTML = `
      <div class="tf-loading">
        <div class="tf-spinner"></div>
        <h2>${esc(COPY.loadingHeading)}</h2>
        <p>${esc(COPY.loadingText)}</p>
      </div>
    `;
  }

  /* ---------------- RENDER: RESULTS ---------------- */
  // Shopify outputs the offer text as rich text in a wrapper and <p>; Fiaz's
  // banner is one inline run of text with <b>. Unwrap it to match.
  function offerTextHtml(html) {
    const tpl = document.createElement("template");
    tpl.innerHTML = html || "";
    tpl.content.querySelectorAll("strong").forEach((el) => {
      const b = document.createElement("b");
      b.innerHTML = el.innerHTML;
      el.replaceWith(b);
    });
    const paragraphs = Array.from(tpl.content.querySelectorAll("p"));
    return paragraphs.length ? paragraphs.map((p) => p.innerHTML.trim()).join(" ") : esc(tpl.content.textContent.trim());
  }

  function renderResults() {
    const results = getRecommendations(state.answers);
    const [best, second, third, ...rest] = results;

    const goToQuiz = (e) => {
      if (e) e.preventDefault();
      state.step = "q";
      state.qIndex = 0;
      render();
    };

    if (!best) {
      app.innerHTML = `
        <section class="tf-results__head">
          <div class="tf-results__eyebrow">${esc(COPY.resultsEyebrow)}</div>
          <h1>${esc(COPY.noMatchesHeading)}</h1>
          <p>${esc(COPY.noMatchesText)}</p>
          <a href="#" id="change-answers" class="tf-change">${icon("arrowLeft", "ic")} Change my answers</a>
        </section>
      `;
      app.querySelector("#change-answers").addEventListener("click", goToQuiz);
      return;
    }

    // Change: the offer banner renders only when the page data includes it
    // ("Show the offer" ticked, UK market).
    app.innerHTML = `
      <section class="tf-results__head">
        <div class="tf-results__eyebrow">${esc(COPY.resultsEyebrow)}</div>
        <h1>${esc(COPY.resultsHeading)}</h1>
        <p>${esc(COPY.resultsText)}</p>
        <a href="#" id="change-answers" class="tf-change">${icon("arrowLeft", "ic")} Change my answers</a>
      </section>

      ${renderBestMatch(best)}

      ${second ? `
        <div class="tf-section-label">${esc(COPY.moreMatchesHeading)}</div>
        <div class="tf-grid">
          ${renderCard(second, COPY.secondLabel)}
          ${renderCard(third, COPY.thirdLabel)}
          ${rest.map((r) => renderCard(r, COPY.otherLabel)).join("")}
        </div>
      ` : ""}

      <div class="tf-footer-cta">
        <p>Not quite right? Refine your answers and we'll re-match you instantly.</p>
        <button class="btn btn-ghost" id="change-answers-2">${icon("arrowLeft")} Change My Answers</button>
      </div>

      ${OFFER ? `
        <div class="tf-urgency" id="urgencyBanner">
          <div class="tf-urgency__text">
            ${offerTextHtml(OFFER.textHtml)}
            <span class="tf-urgency__code">${esc(OFFER.code)}</span> ${esc(OFFER.textAfter)}
          </div>
          <div class="tf-urgency__right">
            <span class="tf-urgency__label">${esc(OFFER.timerLabel)}</span>
            <span class="tf-urgency__timer" id="urgencyTimer">15:00</span>
          </div>
        </div>
      ` : ""}
    `;

    app.querySelector("#change-answers").addEventListener("click", goToQuiz);
    app.querySelector("#change-answers-2").addEventListener("click", goToQuiz);

    if (OFFER) startUrgencyCountdown();
  }

  function startUrgencyCountdown() {
    const DURATION_MS = 15 * 60 * 1000;
    const deadline = Date.now() + DURATION_MS;
    const timerEl = app.querySelector("#urgencyTimer");
    const bannerEl = app.querySelector("#urgencyBanner");
    if (!timerEl || !bannerEl) return;

    function tick() {
      const remaining = Math.max(0, deadline - Date.now());
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      timerEl.textContent = `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
      if (remaining <= 0) {
        bannerEl.classList.add("is-expired");
        timerEl.textContent = "00:00";
        clearInterval(urgencyInterval);
        urgencyInterval = null;
      }
    }
    tick();
    urgencyInterval = setInterval(tick, 1000);
  }

  function priceHtml(product, sizeClass = "") {
    return `
      <div class="tf-price ${sizeClass}">
        <span class="tf-price__now">${esc(product.priceText)}</span>
        ${product.wasPriceText ? `<span class="tf-price__was">${esc(product.wasPriceText)}</span>` : ""}
      </div>
    `;
  }

  function reasonsHtml(reasons, max) {
    return `<ul class="tf-reasons">${reasons
      .slice(0, max)
      .map((r) => `<li>${icon("check")}<span>${esc(r)}</span></li>`)
      .join("")}</ul>`;
  }

  function renderBestMatch(entry) {
    const { product, reasons } = entry;
    return `
      <div class="tf-best">
        <div class="tf-best__media">
          <span class="mc-badge" style="top:16px;left:16px;"><span class="pill pill-purple-solid">${icon("medal", "ic")} ${esc(COPY.bestMatchLabel)}</span></span>
          <img src="${esc(product.image)}" alt="${esc(product.name)}" />
        </div>
        <div class="tf-best__body">
          <h2 class="tf-best__name">${esc(product.name)}</h2>
          <div class="tf-best__meta">
            <span class="pill pill-neutral">${esc(product.size)}</span>
            <span class="pill pill-purple">${esc(product.fabricLabel)}</span>
            <span class="pill pill-neutral">Sleeps ${esc(product.capacity)}</span>
          </div>
          ${priceHtml(product)}
          <div class="tf-why">${esc(COPY.whyLabel)}</div>
          ${reasonsHtml(reasons, 4)}
          <div class="tf-best__actions">
            <a class="btn btn-primary" href="${esc(product.url)}" target="_blank" rel="noopener">${esc(COPY.viewButton)} ${icon("arrowRight", "ic")}</a>
          </div>
        </div>
      </div>
    `;
  }

  function renderCard(entry, rankLabel) {
    if (!entry) return "";
    const { product, reasons } = entry;
    return `
      <div class="tf-card">
        <div class="tf-card__media">
          <span class="tf-card__rank"><span class="pill pill-purple-solid">${esc(rankLabel)}</span></span>
          <img src="${esc(product.image)}" alt="${esc(product.name)}" />
        </div>
        <div class="tf-card__body">
          <h3 class="tf-card__name">${esc(product.name)}</h3>
          <div class="tf-card__meta">
            <span class="pill pill-neutral">${esc(product.size)}</span>
            <span class="pill pill-purple">${esc(product.fabricLabel)}</span>
          </div>
          ${priceHtml(product)}
          ${reasonsHtml(reasons, 3)}
          <div class="tf-card__actions">
            <a class="btn btn-outline btn-block" href="${esc(product.url)}" target="_blank" rel="noopener">${esc(COPY.viewButton)} ${icon("arrowRight", "ic")}</a>
          </div>
        </div>
      </div>
    `;
  }

  /* ---------------- INIT ---------------- */
  render();
})();
